// Outils partagés du module « vie » : chemins d'API, libellés, statuts (icône + mot), actions.
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useGet } from "@core/hooks";
import { upload } from "@core/api";
import { date, dateTime } from "@core/format";
import { Badge, Select, useToast } from "@core/ui";

// ---------------------------------------------------------------------------
// Chemins

export const orgApi = (orgId: string, path: string) => `/api/v1/orgs/${orgId}/vie${path}`;
export const meLease = (leaseId: string, path: string) => `/api/v1/me/vie/leases/${leaseId}${path}`;
export const delegApi = (id: string, path = "") => `/api/v1/vie/delegations/${id}${path}`;
export const docUrl = (id: string) => `/api/v1/documents/${id}/fichier`;
export const orgPath = (orgId: string, path: string) => `/espace/${orgId}/${path}`;

/** Valeur d'un champ date-heure local → instant ISO (fuseau du navigateur). */
export const toIso = (local: string) => new Date(local).toISOString();
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// ---------------------------------------------------------------------------
// Libellés

type Opt = { value: string; label: string };
const opts = (m: Record<string, string>): Opt[] => Object.entries(m).map(([value, label]) => ({ value, label }));

export const CATEGORIES: Record<string, string> = {
  plomberie: "Plomberie, fuite",
  electricite: "Électricité",
  chauffage: "Chauffage, eau chaude",
  serrurerie: "Serrurerie, porte",
  electromenager: "Électroménager",
  humidite: "Humidité, ventilation",
  nuisibles: "Nuisibles",
  menuiserie: "Fenêtres, volets, menuiserie",
  parties_communes: "Parties communes",
  autre: "Autre",
};
export const URGENCIES: Record<string, string> = { normale: "Normale", importante: "Importante", urgente: "Urgente" };
export const PRIORITIES: Record<string, string> = { basse: "Basse", normale: "Normale", haute: "Haute" };
export const INCIDENT_STATUS: Record<string, string> = {
  signale: "Signalé",
  info_demandee: "Informations demandées",
  pris_en_charge: "Pris en charge",
  prevu: "Intervention prévue",
  resolu: "Résolu",
  annule: "Annulé",
};
export const QUOTE_STATUS: Record<string, string> = { recu: "Reçu, à décider", accepte: "Accepté", refuse: "Refusé", annule: "Annulé" };
export const INTERVENTION_STATUS: Record<string, string> = { planifiee: "Planifiée", realisee: "Réalisée", annulee: "Annulée" };
export const RECOVERABLE: Record<string, string> = {
  a_determiner: "À déterminer",
  recuperable: "Récupérable sur le locataire",
  partiellement_recuperable: "Partiellement récupérable",
  non_recuperable: "Non récupérable",
};
export const EQUIPMENT_KINDS: Record<string, string> = {
  chaudiere: "Chaudière",
  chauffe_eau: "Chauffe-eau",
  pompe_a_chaleur: "Pompe à chaleur",
  climatisation: "Climatisation",
  vmc: "VMC",
  conduit_cheminee: "Conduit de cheminée",
  detecteur_fumee: "Détecteur de fumée",
  electromenager: "Électroménager",
  menuiserie: "Menuiserie",
  toiture: "Toiture",
  autre: "Autre",
};
export const MAINTAINERS: Record<string, string> = { a_preciser: "À préciser", bailleur: "Bailleur", locataire: "Locataire", copropriete: "Copropriété" };
export const MAINTENANCE_EVENTS: Record<string, string> = { entretien: "Entretien", reparation: "Réparation", remplacement: "Remplacement", controle: "Contrôle", mise_en_service: "Mise en service" };
export const DEPARTURE_STATUS: Record<string, string> = { recu: "Congé reçu, à confirmer", confirme: "Date de fin confirmée", annule: "Annulé", termine: "Clôturé" };
export const VISIT_STATUS: Record<string, string> = { proposee: "En attente du locataire", acceptee: "Acceptée", refusee: "Refusée", annulee: "Annulée", realisee: "Effectuée" };
export const DEPOSIT_STATUS: Record<string, string> = { brouillon: "Brouillon", valide: "Validé", restitue_partiellement: "Restitué en partie", restitue: "Restitué", clos: "Clos" };
export const DEDUCTION_KINDS: Record<string, string> = { reparation: "Réparation", nettoyage: "Nettoyage", remplacement: "Remplacement", cles: "Clés", autre: "Autre" };
export const AUTH_KINDS: Record<string, string> = {
  travaux: "Travaux",
  amenagement: "Aménagement",
  equipement: "Équipement",
  sous_location: "Sous-location",
  hebergement: "Hébergement d’un proche",
  activite_professionnelle: "Activité professionnelle",
  autre: "Autre",
};
export const AUTH_STATUS: Record<string, string> = {
  en_attente: "En attente de réponse",
  accordee: "Accordée",
  accordee_sous_conditions: "Accordée sous conditions",
  refusee: "Refusée",
  retiree: "Retirée",
};
export const CLAIM_KINDS: Record<string, string> = {
  degat_des_eaux: "Dégât des eaux",
  incendie: "Incendie",
  vol_cambriolage: "Vol, cambriolage",
  bris_de_glace: "Bris de glace",
  catastrophe_naturelle: "Catastrophe naturelle",
  tempete_grele: "Tempête, grêle",
  responsabilite_civile: "Responsabilité civile",
  autre: "Autre",
};
export const CLAIM_STATUS: Record<string, string> = {
  ouvert: "Ouvert",
  declare: "Déclaré",
  expertise: "Expertise",
  indemnisation_en_cours: "Indemnisation en cours",
  indemnise: "Indemnisé",
  refuse: "Refusé par l’assureur",
  clos: "Clos",
};
export const CLAIM_ROLES: Record<string, string> = {
  assureur_proprietaire: "Assureur du propriétaire",
  assureur_locataire: "Assureur du locataire",
  assureur_copropriete: "Assureur de la copropriété",
  assureur_tiers: "Assureur d’un tiers",
  expert: "Expert",
  syndic: "Syndic",
  autre: "Autre",
};
export const CLAIM_EVENTS: Record<string, string> = { declaration: "Déclaration", expertise: "Expertise", indemnite: "Indemnité reçue", refus: "Refus", relance: "Relance", note: "Note" };
export const KEY_KINDS: Record<string, string> = { cle: "Clé", badge: "Badge", telecommande: "Télécommande", carte: "Carte", code: "Code" };
export const KEY_STATUS: Record<string, string> = { en_stock: "Chez le bailleur", remise: "Remis", perdue: "Perdu", desactivee: "Désactivé" };
export const KEY_HOLDERS: Record<string, string> = { locataire: "Locataire", tiers: "Tiers", gardien: "Gardien", syndic: "Syndic", artisan: "Artisan", bailleur: "Bailleur" };
export const KEY_MOVEMENTS: Record<string, string> = {
  creation: "Enregistrement",
  remise: "Remise",
  restitution: "Restitution",
  perte: "Perte",
  remplacement: "Remplacement",
  desactivation: "Désactivation",
};
export const DELEGATION_STATUS: Record<string, string> = { invitee: "Invitation envoyée", active: "Active", a_venir: "À venir", expiree: "Expirée", revoquee: "Révoquée" };
export const TASK_STATUS: Record<string, string> = { a_faire: "À faire", en_cours: "En cours", fait: "Fait", annule: "Annulé" };
export const AMENDMENT_KINDS: Record<string, string> = {
  sortie_colocataire: "Sortie d’un colocataire",
  entree_colocataire: "Arrivée d’un colocataire",
  remplacement_colocataire: "Remplacement d’un colocataire",
  quotes_parts: "Nouvelles quotes-parts",
  changement_garant: "Changement de garant",
  autre: "Autre modification",
};
export const AMENDMENT_STATUS: Record<string, string> = { brouillon: "Brouillon", a_signer: "À signer", signe: "Signé", annule: "Annulé" };
export const TRANSFER_KINDS: Record<string, string> = { vente: "Vente du logement", changement_gestionnaire: "Changement de gestionnaire", autre: "Autre" };
export const TRANSFER_STATUS: Record<string, string> = { en_attente: "En attente", acceptee: "Acceptée", refusee: "Refusée", annulee: "Annulée" };
export const DUE_KINDS: Record<string, string> = { echeance: "Échéance", rappel: "Rappel", rendez_vous: "Rendez-vous", suivi: "Suivi" };

export const options = opts;

// ---------------------------------------------------------------------------
// Statuts : icône + mot, jamais la couleur seule

type Tone = "conforme" | "a-confirmer" | "anomalie" | "manquant" | "info";
const TONE_BY_STATUS: Record<string, Tone> = {
  signale: "a-confirmer",
  info_demandee: "a-confirmer",
  pris_en_charge: "info",
  prevu: "info",
  resolu: "conforme",
  annule: "manquant",
  annulee: "manquant",
  recu: "a-confirmer",
  accepte: "conforme",
  refuse: "anomalie",
  planifiee: "info",
  realisee: "conforme",
  confirme: "info",
  termine: "conforme",
  proposee: "a-confirmer",
  acceptee: "conforme",
  refusee: "anomalie",
  brouillon: "a-confirmer",
  valide: "info",
  restitue_partiellement: "a-confirmer",
  restitue: "conforme",
  clos: "manquant",
  en_attente: "a-confirmer",
  accordee: "conforme",
  accordee_sous_conditions: "conforme",
  retiree: "manquant",
  ouvert: "a-confirmer",
  declare: "info",
  expertise: "info",
  indemnisation_en_cours: "info",
  indemnise: "conforme",
  en_stock: "info",
  remise: "info",
  perdue: "anomalie",
  desactivee: "manquant",
  invitee: "a-confirmer",
  active: "conforme",
  a_venir: "info",
  expiree: "manquant",
  revoquee: "manquant",
  a_faire: "a-confirmer",
  en_cours: "info",
  fait: "conforme",
  a_signer: "a-confirmer",
  signe: "conforme",
  acceptee_transfert: "conforme",
};

const ICONS: Record<Tone, ReactNode> = {
  conforme: <path d="M8 12.5l2.7 2.7L16 9.8" />,
  "a-confirmer": <path d="M12 7.5v5l3 2" />,
  anomalie: <path d="M12 8v5M12 16.5h.01" />,
  manquant: <path d="M8 12h8" />,
  info: <path d="M12 11v5M12 8h.01" />,
};

/** Statut affiché avec son icône et son libellé. */
export function Status({ value, labels, label }: { value: string; labels?: Record<string, string>; label?: string }) {
  const tone = TONE_BY_STATUS[value] ?? "info";
  return (
    <span className={`bv-statut bv-statut-${tone}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" strokeDasharray={tone === "manquant" ? "3 3" : undefined} />
        {ICONS[tone]}
      </svg>
      {label ?? labels?.[value] ?? value}
    </span>
  );
}

export function UrgencyBadge({ value }: { value: string }) {
  if (value === "urgente") return <Badge tone="erreur">Urgent</Badge>;
  if (value === "importante") return <Badge tone="attention">Important</Badge>;
  return <Badge>Normal</Badge>;
}

export function OverdueBadge() {
  return <Badge tone="erreur">En retard</Badge>;
}

/** Mention de règle à valider (versionnée). */
export function RuleNote({ children, version }: { children: ReactNode; version?: string }) {
  return (
    <p className="vie-rule small">
      <span className="vie-rule-tag">Règle à valider{version ? ` · ${version}` : ""}</span> {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Actions

/** Exécute une action serveur avec indicateur d'attente, erreur et confirmation. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function run<T>(fn: () => Promise<T>, ok?: string): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (ok) toast(ok);
      return r;
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run, setError };
}

/** Envoie un fichier avec des champs texte ; renvoie la réponse de l'API. */
export function sendFile<T = any>(path: string, file: File, fields: Record<string, string> = {}) {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append("file", file);
  return upload<T>(path, form);
}

// ---------------------------------------------------------------------------
// Références de l'espace (logements, baux)

export type LeaseRef = { id: string; property_id: string; property_label: string; status: string; regime: string; start_on: string; end_on: string | null; tenants: string[] };
export type PropertyRef = { id: string; label: string; kind: string };

export function useRefs(orgId: string) {
  const q = useGet<{ properties: PropertyRef[]; leases: LeaseRef[] }>(orgApi(orgId, "/references"));
  return { properties: q.data?.properties ?? [], leases: q.data?.leases ?? [], loading: q.isLoading };
}

export function leaseLabel(l: LeaseRef) {
  const who = l.tenants.length ? l.tenants.join(", ") : "sans locataire en place";
  return `${l.property_label} — ${who} (depuis le ${date(l.start_on)})`;
}

export function LeaseSelect({ orgId, value, onChange, label = "Bail", activeOnly = true, error }: { orgId: string; value: string; onChange: (v: string) => void; label?: string; activeOnly?: boolean; error?: string }) {
  const { leases } = useRefs(orgId);
  const list = leases.filter((l) => !activeOnly || ["actif", "preavis", "signe"].includes(l.status));
  return <Select label={label} value={value} error={error} onChange={(e) => onChange(e.target.value)} options={[{ value: "", label: "Choisir…" }, ...list.map((l) => ({ value: l.id, label: leaseLabel(l) }))]} />;
}

export function PropertySelect({ orgId, value, onChange, label = "Logement", error, emptyLabel }: { orgId: string; value: string; onChange: (v: string) => void; label?: string; error?: string; emptyLabel?: string }) {
  const { properties } = useRefs(orgId);
  return (
    <Select
      label={label}
      value={value}
      error={error}
      onChange={(e) => onChange(e.target.value)}
      options={[{ value: "", label: emptyLabel ?? (label.startsWith("Filtrer") ? "Tous les logements" : "Choisir…") }, ...properties.map((p) => ({ value: p.id, label: p.label }))]}
    />
  );
}

// ---------------------------------------------------------------------------
// Chronologie

export type TimelineItem = { id: string; at: string; who?: string; text: ReactNode; tag?: ReactNode };

export function Timeline({ items, empty = "Aucun élément pour l’instant." }: { items: TimelineItem[]; empty?: string }) {
  if (!items.length) return <p className="muted">{empty}</p>;
  return (
    <ol className="vie-timeline">
      {items.map((i) => (
        <li key={i.id}>
          <p className="vie-timeline-meta small muted">
            {dateTime(i.at)}
            {i.who ? ` · ${i.who}` : ""} {i.tag}
          </p>
          <div className="vie-timeline-text">{i.text}</div>
        </li>
      ))}
    </ol>
  );
}

export function DocLinks({ docs }: { docs: { id: string; filename: string; title?: string; size_bytes?: number }[] }) {
  if (!docs?.length) return null;
  return (
    <ul className="list-plain vie-docs">
      {docs.map((d) => (
        <li key={d.id}>
          <a href={docUrl(d.id)} target="_blank" rel="noreferrer">
            {d.title || d.filename}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="small">
      ← {children}
    </Link>
  );
}
